/** Paramètres → Numérotation : préfixe, date, compteur. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { useCompany } from "../../company/CompanyProvider";
import {
  formatNumberPreview,
  numberingFromCompany,
  NUMBER_DATE_MODES,
  NUMBER_PADS,
  type CompanyNumbering,
  type NumberDateMode,
  type NumberDocType,
  type NumberSeries,
} from "../../numbering/types";
import { isCompanyOwner } from "./owner";

const SAMPLE_DAY = "2026-09-16";

const SERIES: { id: NumberDocType; label: string }[] = [
  { id: "invoice", label: "Factures" },
  { id: "quote", label: "Devis" },
  { id: "credit_note", label: "Avoirs" },
  { id: "recurring", label: "Factures récurrentes" },
  { id: "payment", label: "Paiements" },
];

export function NumberingTab() {
  const { company, updateNumbering } = useCompany();
  const [draft, setDraft] = useState<CompanyNumbering | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(numberingFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);
  const prod = (company.environment || "production") !== "lab";

  function patch(type: NumberDocType, next: Partial<NumberSeries>) {
    setDraft((cur) => {
      if (!cur) return cur;
      const series = { ...cur[type], ...next };
      if (series.date_mode === "none") series.reset_yearly = false;
      return { ...cur, [type]: series };
    });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateNumbering(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card fx-num">
      <header className="fx-co-card__head">
        <h2>Numérotation</h2>
        <p>
          Factures, devis, avoirs : numéro à la validation. Récurrence et paiements : numéro à la création.
          En production, le compteur d’une série déjà utilisée ne se recule pas. Conservation : 10 ans.
        </p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre.</p>
        ) : null}
      </header>

      {SERIES.map((s) => (
        <SeriesBlock
          key={s.id}
          label={s.label}
          series={draft[s.id]}
          owner={owner}
          lockNext={prod && draft[s.id].issued === true}
          onPatch={(next) => patch(s.id, next)}
        />
      ))}

      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function SeriesBlock({
  label,
  series,
  owner,
  lockNext,
  onPatch,
}: {
  label: string;
  series: NumberSeries;
  owner: boolean;
  lockNext: boolean;
  onPatch: (next: Partial<NumberSeries>) => void;
}) {
  const preview = formatNumberPreview(series, SAMPLE_DAY);

  return (
    <section className="fx-num-block">
      <span>{label}</span>
      <div className="fx-num-fields">
        <Field
          label="Préfixe"
          value={series.prefix}
          maxLength={8}
          disabled={!owner}
          placeholder="Aucun"
          onChange={(e) => onPatch({ prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) })}
        />
        <Field
          label="Prochain"
          type="number"
          min={1}
          max={999999}
          value={String(series.next_number)}
          disabled={!owner || lockNext}
          hint={lockNext ? "Série déjà utilisée — le compteur ne recule pas." : undefined}
          onChange={(e) => onPatch({ next_number: Math.min(999999, Math.max(1, Number(e.target.value) || 1)) })}
        />
      </div>
      <div className="fx-fmt-picks fx-num-picks" role="radiogroup" aria-label={`Date — ${label}`}>
        {NUMBER_DATE_MODES.map((m) => (
          <Pick
            key={m.id}
            checked={series.date_mode === m.id}
            label={m.label}
            sample={formatNumberPreview({ ...series, date_mode: m.id }, SAMPLE_DAY)}
            disabled={!owner}
            onPick={() => onPatch({ date_mode: m.id as NumberDateMode })}
          />
        ))}
      </div>
      <div className="fx-fmt-picks fx-num-pads" role="radiogroup" aria-label={`Chiffres — ${label}`}>
        {NUMBER_PADS.map((p) => (
          <Pick
            key={p}
            checked={series.pad === p}
            label={`${p} chiffres`}
            sample={formatNumberPreview({ ...series, pad: p }, SAMPLE_DAY)}
            disabled={!owner}
            onPick={() => onPatch({ pad: p })}
          />
        ))}
      </div>
      <CheckField
        label="Reprendre à 1 chaque année"
        checked={series.reset_yearly}
        disabled={!owner || series.date_mode === "none"}
        onChange={(next) => onPatch({ reset_yearly: next })}
      />
      <aside className="fx-fmt-preview" aria-label={`Aperçu ${label}`}>
        <strong>Prochaine pièce</strong>
        <p>{preview}</p>
      </aside>
    </section>
  );
}

function Pick({
  checked,
  label,
  sample,
  disabled,
  onPick,
}: {
  checked: boolean;
  label: string;
  sample: string;
  disabled?: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      className={checked ? "is-on" : ""}
      disabled={disabled}
      onClick={onPick}
    >
      <strong>{label}</strong>
      <em>{sample}</em>
    </button>
  );
}
